import React from 'react';
import Select from 'react-select';
import { brandSelectTheme } from './reactSelectTheme';

/**
 * react-select in the app's clothes: brand-green theme, 34px control with a
 * hairline border (the same numbers as the text inputs beside it), menu in a
 * body portal so it clears modals and sticky bars.
 *
 * Takes a scalar `value` and a flat `options` list, and reports changes as an
 * event-shaped object `{ target: { name, value } }`, so a form's existing
 * `handleChange` — Formik's or a hand-rolled one reading e.target — keeps
 * working when a native <select> is swapped for this.
 */

const control = (borderColor) => (base) => ({
    ...base,
    minHeight: 34,
    borderRadius: 8,
    borderColor,
    fontSize: '0.875rem',
    boxShadow: 'none',
});

const baseStyles = {
    // Fill the field like a native input would, also inside a flex row.
    container: (base) => ({ ...base, width: '100%', flex: 1, minWidth: 0 }),
    control: control('#d1d5db'),
    menuPortal: (base) => ({ ...base, zIndex: 1080 }),
};

const invalidStyles = { ...baseStyles, control: control('#dc3545') };

export default function BrandSelect({
    name,
    inputId,
    options = [],
    value,
    onChange,
    onBlur,
    placeholder = 'Välj…',
    isInvalid = false,
    isDisabled = false,
    isClearable = false,
    isSearchable = false,
    className,
}) {
    const selected = options.find((o) => String(o.value) === String(value ?? '')) || null;
    return (
        <Select
            inputId={inputId}
            name={name}
            options={options}
            value={selected}
            onChange={(option) => onChange?.({ target: { name, value: option ? option.value : '' } }, option)}
            onBlur={() => onBlur?.({ target: { name } })}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isClearable={isClearable}
            isSearchable={isSearchable}
            theme={brandSelectTheme}
            styles={isInvalid ? invalidStyles : baseStyles}
            classNamePrefix="react-select"
            className={className}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            noOptionsMessage={() => 'Inga alternativ'}
        />
    );
}

/**
 * For call sites written as `<SelectField>…<option>…</option></SelectField>`:
 * turns the option children into a flat list. A leading "-- Välj x --" option
 * becomes the placeholder rather than a selectable entry.
 */
export function optionsFromChildren(children) {
    const options = [];
    let placeholder;
    const text = (node) => React.Children.toArray(node).map((c) => (typeof c === 'object' ? text(c.props?.children) : String(c))).join('').replace(/\s+/g, ' ').trim();
    React.Children.forEach(children, function walk(child) {
        if (!child) return;
        if (Array.isArray(child)) { child.forEach(walk); return; }
        if (child.type === 'option') {
            const label = text(child.props.children);
            const m = /^-{2,}\s*(.*?)\s*-{2,}$/.exec(label);
            if (m && (child.props.value === '' || child.props.value === undefined)) { placeholder = m[1]; return; }
            options.push({ value: child.props.value ?? label, label });
        } else if (child.props?.children) {
            React.Children.forEach(child.props.children, walk);
        }
    });
    return { options, placeholder };
}
